import clsx from "clsx";
import merge from "lodash/merge";
import { FunctionComponent, JSX } from "react";

import CompactContentCard from "@ui/components/content-cards/CompactContentCard";
import { type ContentCardArticle } from "@ui/components/content-cards/content-cards.types";
import { type Pattern } from "@ui/components/patterns/patterns.types";
import * as altStyles from "@ui/components/patterns/ThreeGridAltPattern/ThreeGridAltPattern.css";
import * as standardStyles from "@ui/components/patterns/ThreeGridPattern/ThreeGridPattern.css";
import { useComponentConfig } from "@ui/context/component-config-context";
import { sharedStyles } from "@ui/shared-styles";
import { getContexts } from "@ui/utils/patterns";

import * as styles from "./ThreeGridAltOverflowPattern.css";

export interface Props extends Pattern {
  componentContext?: string;
  desktopCardContainerClassName?: string;
  desktopCardAlignTop?: boolean;
}

const ThreeGridAltOverflowPattern: FunctionComponent<Props> = (props) => {
  const threeGridAltOverflowPatternConfig = useComponentConfig(
    "threeGridAltOverflowPattern",
    props.componentContext || "",
  );

  const {
    cardsData,
    gaComponentName = "three_grid_alt_overflow_pattern",
    mobileCardContext = "threeGridAltPattern",
    desktopCardContext,
    showGridBorders = false,
    mobileCardClassName,
    desktopCardClassName,
    desktopContainerClassName,
    desktopCardContainerClassName,
    desktopCardAlignTop = false,
  } = merge({}, threeGridAltOverflowPatternConfig, props);

  const { mobileContext, desktopContext } = getContexts(
    cardsData.length,
    mobileCardContext,
    desktopCardContext,
  );

  const getDesktopCard = (entry: ContentCardArticle, idx: number) => {
    return (
      <div
        className={clsx(
          altStyles.desktopCardContainer,
          showGridBorders && standardStyles.gridBorders,
          desktopCardContainerClassName,
        )}
        key={entry.id}
      >
        <CompactContentCard
          {...entry}
          componentContext={desktopContext[idx]}
          gaComponentName={gaComponentName}
          className={clsx(altStyles.altCard, desktopCardClassName)}
          alignTop={desktopCardAlignTop}
        />
      </div>
    );
  };

  const getMobileCard = (entry: ContentCardArticle, idx: number) => {
    return (
      <CompactContentCard
        {...entry}
        key={entry.id}
        componentContext={mobileContext[idx]}
        gaComponentName={gaComponentName}
        firstChild={idx === 0}
        lastChild={idx === cardsData.length - 1}
        className={mobileCardClassName}
      />
    );
  };

  const desktopCards: Array<JSX.Element> = [];
  const mobileCards: Array<JSX.Element> = [];

  cardsData.forEach((entry, idx) => {
    desktopCards.push(getDesktopCard(entry, idx));
    mobileCards.push(getMobileCard(entry, idx));
  });

  return (
    <div>
      <div className={clsx(styles.desktopContainer, desktopContainerClassName)}>
        {desktopCards}
      </div>
      <div className={standardStyles.mobileContainer}>
        <div
          className={clsx(
            styles.overflowInnerContainer,
            sharedStyles.layout.hideScrollbar,
          )}
        >
          {mobileCards}
        </div>
      </div>
    </div>
  );
};

export default ThreeGridAltOverflowPattern;
